import React, { useState, useEffect, useRef, useCallback } from 'react';
import Navbar from '../components/Navbar';
import axios from 'axios';
import config from '../config';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

function Intelligence() {
  const [stats, setStats] = useState(null);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dataError, setDataError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  // Scan trends
  const [scanTrend, setScanTrend] = useState([]);
  const [scanTrendLoading, setScanTrendLoading] = useState(false);
  const [scanTrendError, setScanTrendError] = useState('');

  // AI Analysis
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');
  const [aiReportDate, setAiReportDate] = useState(null);

  // Heatmap
  const [scanLocations, setScanLocations] = useState([]);
  const [heatmapLoading, setHeatmapLoading] = useState(false);
  const [heatmapError, setHeatmapError] = useState('');
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);

  // Consumer Reports
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsError, setReportsError] = useState('');

  // ── INITIAL DATA LOAD ─────────────────────────────────────────────────────
  useEffect(() => {
    fetchData();
    fetchScanTrends();
  }, []);

  const fetchData = async () => {
    setDataError('');
    try {
      const [statsRes, batchesRes] = await Promise.all([
        axios.get(`${config.API_URL}/stats`),
        axios.get(`${config.API_URL}/batches`),
      ]);
      setStats(statsRes.data.stats);
      setBatches(batchesRes.data.batches || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      setDataError('Failed to load data. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  // ── TAB 1: SCAN TRENDS ───────────────────────────────────────────────────
  const fetchScanTrends = async () => {
    setScanTrendLoading(true);
    setScanTrendError('');
    try {
      // Try /scans/trends first (newer endpoint)
      const res = await axios.get(`${config.API_URL}/scans/trends`);
      if (res.data.success && res.data.trend && res.data.trend.length > 0) {
        setScanTrend(res.data.trend);
        setScanTrendLoading(false);
        return;
      }
    } catch (err) {
      console.warn('/scans/trends failed, trying /scans/trend:', err.message);
    }

    try {
      // Fallback to /scans/trend (older endpoint)
      const res = await axios.get(`${config.API_URL}/scans/trend`);
      if (res.data.success && res.data.trend && res.data.trend.length > 0) {
        setScanTrend(res.data.trend);
        setScanTrendLoading(false);
        return;
      }
    } catch (err) {
      console.warn('/scans/trend also failed:', err.message);
    }

    setScanTrendError('Scan trend data is currently unavailable.');
    setScanTrendLoading(false);
  };

  // ── TAB 5: AI ANALYSIS ───────────────────────────────────────────────────
  const loadAiAnalysis = useCallback(async () => {
    setAiLoading(true);
    setAiError('');
    // Step 1: Try cached report
    try {
      const res = await axios.get(`${config.API_URL}/intelligence/latest`);
      if (res.data.success && res.data.report && res.data.report.analysis) {
        setAiAnalysis(res.data.report.analysis);
        setAiReportDate(res.data.report.date || null);
        setAiLoading(false);
        return;
      }
    } catch (err) {
      console.warn('No cached report:', err.message);
    }
    // No cached — show empty state so user can click Generate
    setAiLoading(false);
  }, []);

  const generateFreshAnalysis = async () => {
    setAiLoading(true);
    setAiError('');

    // Try /intelligence/trigger (server does everything)
    try {
      const res = await axios.get(`${config.API_URL}/intelligence/trigger`);
      if (res.data.success && res.data.analysis) {
        setAiAnalysis(res.data.analysis);
        setAiReportDate(new Date().toISOString().split('T')[0]);
        setAiLoading(false);
        return;
      }
    } catch (err) {
      console.warn('trigger failed:', err.message);
    }

    // Fallback: POST /intelligence/analyse
    try {
      const [statsRes, batchesRes] = await Promise.all([
        axios.get(`${config.API_URL}/stats`),
        axios.get(`${config.API_URL}/batches`),
      ]);
      const res = await axios.post(`${config.API_URL}/intelligence/analyse`, {
        batches: batchesRes.data.batches || [],
        stats: statsRes.data.stats,
      });
      if (res.data.success && res.data.analysis) {
        setAiAnalysis(res.data.analysis);
        setAiReportDate('Live');
        setAiLoading(false);
        return;
      }
    } catch (err) {
      console.error('All AI methods failed:', err);
    }

    setAiError('AI analysis failed. Please check that the API is running and the Anthropic key is configured, then try again.');
    setAiLoading(false);
  };

  // Auto-load AI when tab opens
  useEffect(() => {
    if (activeTab === 'ai' && !aiAnalysis && !aiLoading && !aiError) {
      loadAiAnalysis();
    }
  }, [activeTab]);

  // ── TAB 6: SCAN HEATMAP ──────────────────────────────────────────────────
  const fetchScanLocations = useCallback(async () => {
    setHeatmapLoading(true);
    setHeatmapError('');
    try {
      const res = await axios.get(`${config.API_URL}/scans/locations`);
      if (res.data.success) {
        const locs = res.data.locations || [];
        setScanLocations(locs);
        if (locs.length === 0) {
          setHeatmapError('No scan locations recorded yet. Locations are captured when consumers scan products via Buy Safe with location permission enabled.');
        }
      }
    } catch (err) {
      console.error('Scan locations error:', err);
      setHeatmapError('Failed to load scan locations from the API.');
    } finally {
      setHeatmapLoading(false);
    }
  }, []);

  // Load locations when heatmap tab opens
  useEffect(() => {
    if (activeTab === 'heatmap' && scanLocations.length === 0 && !heatmapLoading && !heatmapError) {
      fetchScanLocations();
    }
  }, [activeTab]);

  // Render Leaflet map
  useEffect(() => {
    // Only render when on heatmap tab with data and a mounted container
    if (activeTab !== 'heatmap') {
      // Cleanup when leaving
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
      return;
    }

    if (scanLocations.length === 0 || !mapContainerRef.current) return;

    // Don't double-init
    if (mapInstanceRef.current) return;

    const timer = setTimeout(() => {
      if (!window.L) {
        setHeatmapError('Leaflet library not loaded. Ensure leaflet CSS and JS are in public/index.html.');
        return;
      }
      try {
        const L = window.L;
        const map = L.map(mapContainerRef.current).setView([-29, 25], 6);
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; OpenStreetMap contributors',
        }).addTo(map);

        scanLocations.forEach(loc => {
          L.circleMarker([loc.lat, loc.lng], {
            radius: 7, fillColor: '#F0B429', color: '#02132b',
            weight: 2, opacity: 1, fillOpacity: 0.85,
          }).addTo(map).bindPopup(
            `<div style="font-family:sans-serif;font-size:12px"><strong>Batch:</strong> ${loc.batchId || 'Unknown'}<br/><strong>Scanned:</strong> ${loc.timestamp ? new Date(loc.timestamp).toLocaleString('en-ZA') : 'N/A'}</div>`
          );
        });

        const bounds = L.latLngBounds(scanLocations.map(l => [l.lat, l.lng]));
        map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
        mapInstanceRef.current = map;
        setTimeout(() => map.invalidateSize(), 300);
      } catch (err) {
        console.error('Map error:', err);
        setHeatmapError('Failed to render map: ' + err.message);
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [activeTab, scanLocations]);

  // ── TAB 7: CONSUMER REPORTS ───────────────────────────────────────────────
  const fetchReports = useCallback(async () => {
    setReportsLoading(true);
    setReportsError('');
    try {
      const res = await axios.get(`${config.API_URL}/reports`);
      if (res.data.success) {
        setReports(res.data.reports || []);
      }
    } catch (err) {
      console.error('Reports error:', err);
      setReportsError('Failed to load consumer reports from the API.');
    } finally {
      setReportsLoading(false);
    }
  }, []);

  // Load reports when tab opens
  useEffect(() => {
    if (activeTab === 'reports' && reports.length === 0 && !reportsLoading && !reportsError) {
      fetchReports();
    }
  }, [activeTab]);

  // ── DERIVED DATA ──────────────────────────────────────────────────────────
  const getProductPerformance = () => {
    return batches.slice(0, 8).map(b => ({
      name: b.product?.length > 20 ? b.product.substring(0, 20) + '...' : b.product || 'Unknown',
      scans: b.scanCount || 0,
      status: b.recalled ? 'recalled' : b.recallPending ? 'pending' : 'active',
    })).sort((a, b) => b.scans - a.scans);
  };

  const getBatchBreakdown = () => {
    if (!stats) return [];
    return [
      { name: 'Active', value: stats.activeBatches || 0, color: '#22A860' },
      { name: 'Recalled', value: stats.recalledBatches || 0, color: '#C0392B' },
      { name: 'Pending', value: stats.pendingRecalls || 0, color: '#F0B429' },
      { name: 'Expiring Soon', value: stats.expiringSoon || 0, color: '#E67E22' },
      { name: 'Expired', value: stats.expired || 0, color: '#8B0000' },
    ].filter(item => item.value > 0);
  };

  const authenticRate = stats?.totalBatches ? Math.round(((stats.totalBatches - (stats.recalledBatches || 0)) / stats.totalBatches) * 100) : 100;

  const getExpiryStatus = (batch) => {
    if (!batch.expiryDate || batch.recalled) return null;
    const expiry = new Date(batch.expiryDate);
    const now = new Date();
    const in30 = new Date(); in30.setDate(in30.getDate() + 30);
    if (expiry < now) return 'expired';
    if (expiry <= in30) return 'expiring';
    return null;
  };

  // ── STYLES ────────────────────────────────────────────────────────────────
  const tabStyle = (tab) => ({
    background: 'none', border: 'none',
    color: activeTab === tab ? '#F0B429' : '#64748B',
    cursor: 'pointer', fontSize: 13,
    fontWeight: activeTab === tab ? 'bold' : 'normal',
    padding: '10px 14px',
    borderBottom: activeTab === tab ? '2px solid #F0B429' : '2px solid transparent',
    transition: 'all 0.2s', whiteSpace: 'nowrap',
  });

  const ttStyle = { backgroundColor: '#1a2a43', border: '1px solid #1E3A6E', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: '#FFFFFF' };

  const spinner = <div style={{ width: 48, height: 48, border: '3px solid #1E3A6E', borderTop: '3px solid #F0B429', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />;

  if (loading) {
    return (<div className="page"><Navbar /><div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}><div style={{ textAlign: 'center' }}>{spinner}<p style={{ color: '#64748B', fontSize: 13 }}>Loading intelligence data...</p></div></div></div>);
  }

  return (
    <div className="page">
      <Navbar />
      <div style={{ padding: 32, maxWidth: 1200, margin: '0 auto' }}>

        <div style={{ marginBottom: 32 }}>
          <h1 style={{ fontSize: 24, fontWeight: 'bold', color: '#FFFFFF' }}>Intelligence</h1>
          <p style={{ color: '#64748B', fontSize: 13, marginTop: 4 }}>Real-time analytics and AI-powered insights on your product authentication programme.</p>
        </div>

        {dataError && (
          <div style={{ backgroundColor: '#2D1515', border: '1px solid #C0392B', borderRadius: 8, padding: 16, marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <p style={{ color: '#E74C3C', fontSize: 13 }}>{dataError}</p>
            <button className="btn-secondary" onClick={() => { setLoading(true); fetchData(); }}>Retry</button>
          </div>
        )}

        {/* KPI Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 32 }}>
          {[
            { label: 'Total Scans', value: stats?.totalScans || 0, color: '#0EA5E9', icon: '📱', sub: 'Consumer verifications' },
            { label: 'Authentic Rate', value: `${authenticRate}%`, color: '#22A860', icon: '✓', sub: 'Batches not recalled' },
            { label: 'Active Batches', value: stats?.activeBatches || 0, color: '#F0B429', icon: '📦', sub: 'Currently on market' },
            { label: 'Pending Approvals', value: stats?.pendingRecalls || 0, color: stats?.pendingRecalls > 0 ? '#C0392B' : '#22A860', icon: '⚡', sub: 'Recalls awaiting approval' },
          ].map((kpi, i) => (
            <div key={i} style={{ backgroundColor: '#1a2a43', border: '1px solid #1E3A6E', borderRadius: 8, padding: 20, borderTop: `3px solid ${kpi.color}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <p style={{ fontSize: 11, color: '#64748B', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 8 }}>{kpi.label}</p>
                  <p style={{ fontSize: 32, fontWeight: 'bold', color: kpi.color, lineHeight: 1 }}>{kpi.value}</p>
                  <p style={{ fontSize: 11, color: '#64748B', marginTop: 8 }}>{kpi.sub}</p>
                </div>
                <span style={{ fontSize: 24 }}>{kpi.icon}</span>
              </div>
            </div>
          ))}
        </div>

        {/* ALL 7 TABS */}
        <div style={{ borderBottom: '1px solid #1E3A6E', marginBottom: 32, display: 'flex', overflowX: 'auto' }}>
          {[
            { key: 'overview', label: 'Scan Trends' },
            { key: 'products', label: 'Product Performance' },
            { key: 'breakdown', label: 'Batch Status' },
            { key: 'activity', label: 'Batch Activity' },
            { key: 'ai', label: '🧠 AI Analysis' },
            { key: 'heatmap', label: '🗺 Scan Heatmap' },
            { key: 'reports', label: '📋 Consumer Reports' },
          ].map(tab => (
            <button key={tab.key} style={tabStyle(tab.key)} onClick={() => setActiveTab(tab.key)}>{tab.label}</button>
          ))}
        </div>

        {/* TAB 1: SCAN TRENDS */}
        {activeTab === 'overview' && (
          <div className="card">
            <h3 style={{ fontSize: 15, fontWeight: 'bold', color: '#F0B429', marginBottom: 8 }}>Scan Volume — Last 30 Days</h3>
            <p style={{ fontSize: 12, color: '#64748B', marginBottom: 24 }}>Consumer verifications from Buy Safe.</p>
            {scanTrendLoading ? <div style={{ textAlign: 'center', padding: 48 }}>{spinner}<p style={{ color: '#64748B', fontSize: 13 }}>Loading...</p></div>
            : scanTrendError ? <div style={{ textAlign: 'center', padding: 48 }}><p style={{ color: '#E67E22', fontSize: 13 }}>{scanTrendError}</p></div>
            : <ResponsiveContainer width="100%" height={300}><LineChart data={scanTrend}><CartesianGrid strokeDasharray="3 3" stroke="#1E3A6E" /><XAxis dataKey="date" tick={{ fill: '#64748B', fontSize: 11 }} tickLine={false} axisLine={{ stroke: '#1E3A6E' }} interval={4} /><YAxis tick={{ fill: '#64748B', fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} /><Tooltip contentStyle={ttStyle} /><Line type="monotone" dataKey="scans" stroke="#F0B429" strokeWidth={2} dot={false} activeDot={{ r: 4, fill: '#F0B429' }} name="Scans" /></LineChart></ResponsiveContainer>}
          </div>
        )}

        {/* TAB 2: PRODUCT PERFORMANCE */}
        {activeTab === 'products' && (
          <div className="card">
            <h3 style={{ fontSize: 15, fontWeight: 'bold', color: '#F0B429', marginBottom: 8 }}>Scans by Product</h3>
            <p style={{ fontSize: 12, color: '#64748B', marginBottom: 24 }}>Which products consumers verify most.</p>
            {getProductPerformance().length === 0 ? <div style={{ textAlign: 'center', padding: 48 }}><p style={{ color: '#64748B', fontSize: 13 }}>No batch data yet.</p></div> : (
              <>
                <ResponsiveContainer width="100%" height={300}><BarChart data={getProductPerformance()} layout="vertical"><CartesianGrid strokeDasharray="3 3" stroke="#1E3A6E" horizontal={false} /><XAxis type="number" tick={{ fill: '#64748B', fontSize: 11 }} tickLine={false} axisLine={{ stroke: '#1E3A6E' }} allowDecimals={false} /><YAxis type="category" dataKey="name" tick={{ fill: '#CBD5E1', fontSize: 11 }} tickLine={false} axisLine={false} width={160} /><Tooltip contentStyle={ttStyle} /><Bar dataKey="scans" radius={[0, 4, 4, 0]}>{getProductPerformance().map((e, i) => <Cell key={i} fill={e.status === 'recalled' ? '#C0392B' : e.status === 'pending' ? '#F0B429' : '#0EA5E9'} />)}</Bar></BarChart></ResponsiveContainer>
                <div style={{ display: 'flex', gap: 24, justifyContent: 'center', marginTop: 16 }}>
                  {[{ l: 'Active', c: '#0EA5E9' }, { l: 'Pending', c: '#F0B429' }, { l: 'Recalled', c: '#C0392B' }].map(i => <div key={i.l} style={{ display: 'flex', alignItems: 'center', gap: 6 }}><div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: i.c }} /><span style={{ fontSize: 11, color: '#CBD5E1' }}>{i.l}</span></div>)}
                </div>
              </>
            )}
          </div>
        )}

        {/* TAB 3: BATCH STATUS */}
        {activeTab === 'breakdown' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            <div className="card">
              <h3 style={{ fontSize: 15, fontWeight: 'bold', color: '#F0B429', marginBottom: 8 }}>Batch Status Distribution</h3>
              <p style={{ fontSize: 12, color: '#64748B', marginBottom: 24 }}>All batches by current status.</p>
              {getBatchBreakdown().length === 0 ? <div style={{ textAlign: 'center', padding: 48 }}><p style={{ color: '#64748B', fontSize: 13 }}>No data.</p></div> : (
                <ResponsiveContainer width="100%" height={260}><PieChart><Pie data={getBatchBreakdown()} cx="50%" cy="50%" innerRadius={70} outerRadius={110} paddingAngle={3} dataKey="value">{getBatchBreakdown().map((e, i) => <Cell key={i} fill={e.color} />)}</Pie><Tooltip contentStyle={ttStyle} /><Legend iconType="circle" iconSize={8} formatter={v => <span style={{ color: '#CBD5E1', fontSize: 12 }}>{v}</span>} /></PieChart></ResponsiveContainer>
              )}
            </div>
            <div className="card">
              <h3 style={{ fontSize: 15, fontWeight: 'bold', color: '#F0B429', marginBottom: 24 }}>Summary</h3>
              {[
                { label: 'Total Batches', value: stats?.totalBatches || 0, color: '#FFFFFF' },
                { label: 'Active', value: stats?.activeBatches || 0, color: '#22A860' },
                { label: 'Recalled', value: stats?.recalledBatches || 0, color: '#C0392B' },
                { label: 'Pending Recalls', value: stats?.pendingRecalls || 0, color: '#F0B429' },
                { label: 'Expiring Soon', value: stats?.expiringSoon || 0, color: '#E67E22' },
                { label: 'Expired', value: stats?.expired || 0, color: '#8B0000' },
                { label: 'Total Scans', value: stats?.totalScans || 0, color: '#0EA5E9' },
                { label: 'Authentic Rate', value: `${authenticRate}%`, color: '#22A860' },
              ].map(r => <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #1E3A6E' }}><span style={{ fontSize: 13, color: '#64748B' }}>{r.label}</span><span style={{ fontSize: 16, fontWeight: 'bold', color: r.color }}>{r.value}</span></div>)}
            </div>
          </div>
        )}

        {/* TAB 4: BATCH ACTIVITY */}
        {activeTab === 'activity' && (
          <div className="card">
            <h3 style={{ fontSize: 15, fontWeight: 'bold', color: '#F0B429', marginBottom: 8 }}>Batch Activity</h3>
            <p style={{ fontSize: 12, color: '#64748B', marginBottom: 24 }}>All batches with expiry warnings.</p>
            {batches.length === 0 ? <div style={{ textAlign: 'center', padding: 48 }}><p style={{ color: '#64748B', fontSize: 13 }}>No batches yet.</p></div> : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr style={{ borderBottom: '1px solid #1E3A6E' }}>{['Batch ID', 'Product', 'Manufacturer', 'Status', 'Expiry', 'Registered'].map(h => <th key={h} style={{ padding: '12px 8px', fontSize: 11, color: '#64748B', textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>)}</tr></thead>
                <tbody>{batches.map((b, i) => { const es = getExpiryStatus(b); const sl = b.recalled ? 'recalled' : b.recallPending ? 'pending' : 'active'; return (
                  <tr key={i} style={{ borderBottom: '1px solid #1E3A6E' }}>
                    <td style={{ padding: '12px 8px', fontSize: 13, color: '#CBD5E1', fontFamily: 'monospace' }}>{b.batchId}</td>
                    <td style={{ padding: '12px 8px', fontSize: 13, color: '#FFF' }}>{b.product}</td>
                    <td style={{ padding: '12px 8px', fontSize: 13, color: '#64748B' }}>{b.manufacturer}</td>
                    <td style={{ padding: '12px 8px' }}><span style={{ backgroundColor: sl === 'recalled' ? '#C0392B' : sl === 'pending' ? '#F0B429' : '#22A860', padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 'bold', textTransform: 'uppercase', color: '#FFF' }}>{sl}</span></td>
                    <td style={{ padding: '12px 8px', fontSize: 12, color: es === 'expired' ? '#E74C3C' : es === 'expiring' ? '#E67E22' : '#64748B', fontWeight: es ? 'bold' : 'normal' }}>{b.expiryDate ? <>{new Date(b.expiryDate).toLocaleDateString('en-ZA', { dateStyle: 'medium' })}{es === 'expired' && ' ⚠ EXPIRED'}{es === 'expiring' && ' ⚠ EXPIRING'}</> : 'N/A'}</td>
                    <td style={{ padding: '12px 8px', fontSize: 13, color: '#64748B' }}>{b.createdAt ? new Date(b.createdAt).toLocaleDateString('en-ZA', { dateStyle: 'medium' }) : 'N/A'}</td>
                  </tr>); })}</tbody>
              </table>
            )}
          </div>
        )}

        {/* TAB 5: AI ANALYSIS */}
        {activeTab === 'ai' && (
          <div>
            {aiLoading && <div className="card" style={{ textAlign: 'center', padding: 48 }}>{spinner}<p style={{ color: '#F0B429', fontSize: 14, fontWeight: 'bold' }}>Claude AI is analysing your data...</p><p style={{ color: '#64748B', fontSize: 12, marginTop: 8 }}>Scanning batch patterns, anomalies, and risk indicators</p></div>}

            {aiError && !aiLoading && <div className="card" style={{ textAlign: 'center', padding: 48 }}><div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div><p style={{ color: '#E74C3C', fontSize: 14, marginBottom: 16 }}>{aiError}</p><button className="btn-primary" onClick={generateFreshAnalysis}>Try Again</button></div>}

            {!aiAnalysis && !aiLoading && !aiError && (
              <div className="card" style={{ textAlign: 'center', padding: 48 }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>🧠</div>
                <h3 style={{ fontSize: 18, fontWeight: 'bold', color: '#FFF', marginBottom: 8 }}>AI Intelligence Analysis</h3>
                <p style={{ fontSize: 13, color: '#64748B', marginBottom: 24, maxWidth: 400, margin: '0 auto 24px' }}>Powered by Claude AI. Analyses your batch data, scan patterns, and consumer reports to detect anomalies and surface actionable recommendations.</p>
                <button className="btn-primary" onClick={generateFreshAnalysis}>Generate AI Analysis</button>
              </div>
            )}

            {aiAnalysis && !aiLoading && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <p style={{ fontSize: 12, color: '#64748B' }}>{aiReportDate ? `Report: ${aiReportDate}` : 'Analysis loaded'}</p>
                  <button className="btn-secondary" style={{ fontSize: 12, padding: '6px 16px' }} onClick={generateFreshAnalysis} disabled={aiLoading}>🔄 Refresh</button>
                </div>

                <div style={{ background: aiAnalysis.riskLevel === 'HIGH' ? 'linear-gradient(135deg, #2D1515, #1a0a0a)' : aiAnalysis.riskLevel === 'MEDIUM' ? 'linear-gradient(135deg, #2D1F00, #1a1200)' : 'linear-gradient(135deg, #0D2A1A, #051a0e)', border: `1px solid ${aiAnalysis.riskLevel === 'HIGH' ? '#C0392B' : aiAnalysis.riskLevel === 'MEDIUM' ? '#F0B429' : '#22A860'}`, borderRadius: 8, padding: 24, marginBottom: 24, display: 'flex', alignItems: 'center', gap: 24 }}>
                  <div style={{ textAlign: 'center', flexShrink: 0 }}>
                    <div style={{ width: 80, height: 80, borderRadius: '50%', border: `3px solid ${aiAnalysis.riskLevel === 'HIGH' ? '#C0392B' : aiAnalysis.riskLevel === 'MEDIUM' ? '#F0B429' : '#22A860'}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ fontSize: 28, fontWeight: 'bold', color: aiAnalysis.riskLevel === 'HIGH' ? '#E74C3C' : aiAnalysis.riskLevel === 'MEDIUM' ? '#F0B429' : '#22A860' }}>{aiAnalysis.riskScore}</span>
                    </div>
                    <p style={{ fontSize: 11, color: '#64748B', marginTop: 8 }}>RISK SCORE</p>
                  </div>
                  <div>
                    <span style={{ fontSize: 12, fontWeight: 'bold', color: '#FFF', backgroundColor: aiAnalysis.riskLevel === 'HIGH' ? '#C0392B' : aiAnalysis.riskLevel === 'MEDIUM' ? '#F0B429' : '#22A860', padding: '4px 12px', borderRadius: 4 }}>{aiAnalysis.riskLevel} RISK</span>
                    <p style={{ fontSize: 14, color: '#CBD5E1', marginTop: 12 }}>{aiAnalysis.riskSummary}</p>
                    {aiAnalysis.summary && <p style={{ fontSize: 12, color: '#64748B', marginTop: 8 }}>{aiAnalysis.summary}</p>}
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>
                  <div className="card">
                    <h3 style={{ fontSize: 14, fontWeight: 'bold', color: '#F0B429', marginBottom: 16 }}>🚨 Alerts</h3>
                    {!aiAnalysis.alerts?.length ? <p style={{ fontSize: 12, color: '#64748B' }}>No alerts.</p> : aiAnalysis.alerts.map((a, i) => (
                      <div key={i} style={{ backgroundColor: a.type === 'ANOMALY' ? '#2D1515' : a.type === 'WARNING' ? '#2D1F00' : '#0B1D35', border: `1px solid ${a.type === 'ANOMALY' ? '#C0392B' : a.type === 'WARNING' ? '#F0B429' : '#1E3A6E'}`, borderRadius: 6, padding: 12, marginBottom: 8 }}>
                        <p style={{ fontSize: 12, fontWeight: 'bold', color: a.type === 'ANOMALY' ? '#E74C3C' : a.type === 'WARNING' ? '#F0B429' : '#0EA5E9', marginBottom: 4 }}>{a.type} — {a.title}</p>
                        <p style={{ fontSize: 12, color: '#CBD5E1' }}>{a.message}</p>
                        {a.batchId && <p style={{ fontSize: 11, color: '#64748B', marginTop: 4 }}>Batch: {a.batchId}</p>}
                      </div>
                    ))}
                  </div>
                  <div className="card">
                    <h3 style={{ fontSize: 14, fontWeight: 'bold', color: '#F0B429', marginBottom: 16 }}>🔮 Predictions</h3>
                    {!aiAnalysis.predictions?.length ? <p style={{ fontSize: 12, color: '#64748B' }}>No predictions.</p> : aiAnalysis.predictions.map((p, i) => (
                      <div key={i} style={{ backgroundColor: '#0B1D35', border: '1px solid #1E3A6E', borderRadius: 6, padding: 12, marginBottom: 8 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <p style={{ fontSize: 12, fontWeight: 'bold', color: '#FFF' }}>{p.title}</p>
                          <span style={{ fontSize: 10, color: p.confidence === 'HIGH' ? '#22A860' : p.confidence === 'MEDIUM' ? '#F0B429' : '#64748B', backgroundColor: '#1E3A6E', padding: '2px 8px', borderRadius: 4 }}>{p.confidence}</span>
                        </div>
                        <p style={{ fontSize: 12, color: '#64748B' }}>{p.description}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="card">
                  <h3 style={{ fontSize: 14, fontWeight: 'bold', color: '#F0B429', marginBottom: 16 }}>✅ Recommendations</h3>
                  {!aiAnalysis.recommendations?.length ? <p style={{ fontSize: 12, color: '#64748B' }}>No recommendations.</p> : aiAnalysis.recommendations.map((r, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 0', borderBottom: '1px solid #1E3A6E' }}>
                      <span style={{ fontSize: 10, fontWeight: 'bold', color: r.priority === 'HIGH' ? '#C0392B' : r.priority === 'MEDIUM' ? '#F0B429' : '#22A860', backgroundColor: r.priority === 'HIGH' ? '#2D1515' : r.priority === 'MEDIUM' ? '#2D1F00' : '#0D2A1A', padding: '3px 8px', borderRadius: 4, whiteSpace: 'nowrap', marginTop: 2 }}>{r.priority}</span>
                      <p style={{ fontSize: 13, color: '#CBD5E1' }}>{r.action}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* TAB 6: SCAN HEATMAP */}
        {activeTab === 'heatmap' && (
          <div className="card">
            <h3 style={{ fontSize: 15, fontWeight: 'bold', color: '#F0B429', marginBottom: 8 }}>Scan Heatmap — South Africa</h3>
            <p style={{ fontSize: 12, color: '#64748B', marginBottom: 24 }}>Where consumers are scanning products. Each dot is a Buy Safe verification.</p>
            {heatmapLoading ? <div style={{ textAlign: 'center', padding: 48 }}>{spinner}<p style={{ color: '#64748B', fontSize: 13 }}>Loading scan locations...</p></div>
            : heatmapError && scanLocations.length === 0 ? <div style={{ textAlign: 'center', padding: 48 }}><div style={{ fontSize: 48, marginBottom: 16 }}>🗺</div><p style={{ color: '#64748B', fontSize: 13 }}>{heatmapError}</p></div>
            : <>
                <p style={{ fontSize: 12, color: '#64748B', marginBottom: 12 }}>{scanLocations.length} scan location{scanLocations.length !== 1 ? 's' : ''} recorded</p>
                <div ref={mapContainerRef} style={{ height: 450, borderRadius: 8, border: '1px solid #1E3A6E', overflow: 'hidden', backgroundColor: '#0B1D35' }} />
              </>}
          </div>
        )}

        {/* TAB 7: CONSUMER REPORTS */}
        {activeTab === 'reports' && (
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <div>
                <h3 style={{ fontSize: 15, fontWeight: 'bold', color: '#F0B429', marginBottom: 8 }}>Consumer Reports</h3>
                <p style={{ fontSize: 12, color: '#64748B' }}>Reports submitted by consumers through Buy Safe.</p>
              </div>
              {reports.length > 0 && <button className="btn-secondary" style={{ fontSize: 12, padding: '6px 16px' }} onClick={() => { setReports([]); fetchReports(); }}>🔄 Refresh</button>}
            </div>
            {reportsLoading ? <div style={{ textAlign: 'center', padding: 48 }}>{spinner}<p style={{ color: '#64748B', fontSize: 13 }}>Loading reports...</p></div>
            : reportsError ? <div style={{ textAlign: 'center', padding: 48 }}><p style={{ color: '#E74C3C', fontSize: 13 }}>{reportsError}</p><button className="btn-secondary" style={{ marginTop: 12 }} onClick={() => { setReportsError(''); fetchReports(); }}>Retry</button></div>
            : reports.length === 0 ? <div style={{ textAlign: 'center', padding: 48 }}><div style={{ fontSize: 48, marginBottom: 16 }}>📋</div><p style={{ color: '#64748B', fontSize: 14, fontWeight: 'bold', marginBottom: 8 }}>No consumer reports yet</p><p style={{ color: '#64748B', fontSize: 12 }}>Reports will appear here when consumers flag products in Buy Safe.</p></div>
            : <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr style={{ borderBottom: '1px solid #1E3A6E' }}>{['Date', 'Reason', 'Store', 'Batch ID', 'Details', 'Status'].map(h => <th key={h} style={{ padding: '12px 8px', fontSize: 11, color: '#64748B', textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>)}</tr></thead>
                <tbody>{reports.map((r, i) => (
                  <tr key={r.id || i} style={{ borderBottom: '1px solid #1E3A6E' }}>
                    <td style={{ padding: '12px 8px', fontSize: 12, color: '#64748B', whiteSpace: 'nowrap' }}>{r.submittedAt ? new Date(r.submittedAt).toLocaleDateString('en-ZA', { dateStyle: 'medium' }) : 'N/A'}</td>
                    <td style={{ padding: '12px 8px' }}><span style={{ fontSize: 11, fontWeight: 'bold', color: '#FFF', backgroundColor: '#C0392B', padding: '3px 8px', borderRadius: 4 }}>{r.reason}</span></td>
                    <td style={{ padding: '12px 8px', fontSize: 13, color: '#CBD5E1' }}>{r.store || '—'}</td>
                    <td style={{ padding: '12px 8px', fontSize: 12, color: '#0EA5E9', fontFamily: 'monospace' }}>{r.batchId || '—'}</td>
                    <td style={{ padding: '12px 8px', fontSize: 12, color: '#64748B', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.details || '—'}</td>
                    <td style={{ padding: '12px 8px' }}><span style={{ fontSize: 11, fontWeight: 'bold', color: r.status === 'new' ? '#F0B429' : '#22A860', backgroundColor: r.status === 'new' ? '#2D1F00' : '#0D2A1A', padding: '3px 8px', borderRadius: 4, textTransform: 'uppercase' }}>{r.status || 'new'}</span></td>
                  </tr>
                ))}</tbody>
              </table>}
          </div>
        )}

      </div>
    </div>
  );
}

export default Intelligence;